import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowLeft } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import ProjectDetailModal from '../components/ProjectDetailModal';

const API_BASE_URL = 'https://smartsolutionslk.com/api/api.php';
const IMAGE_BASE_URL = 'https://smartsolutionslk.com/uploads/';

const CATEGORY_CONFIG = {
    'All': {
        label: 'All Projects',
        headline: 'All Projects',
        description: 'Explore the full portfolio of work we\'ve delivered across industries, technologies, and continents.',
        accent: 'from-cyan-400 to-cyan-600',
        badge: 'bg-cyan-500',
    },
    'Digital Marketing': {
        label: 'Digital Marketing',
        headline: 'Digital Marketing',
        description: 'Campaigns, social strategies, and data-driven growth stories that delivered measurable results for our clients.',
        accent: 'from-orange-400 to-pink-500',
        badge: 'bg-orange-500',
    },
    'Web & App Engineering': {
        label: 'Web & App Engineering',
        headline: 'Web & App Engineering',
        description: 'High-performance, resilient web and mobile applications built for every platform and every scale.',
        accent: 'from-cyan-400 to-blue-500',
        badge: 'bg-cyan-500',
    },
    'AI & Automation': {
        label: 'AI & Automation',
        headline: 'AI & Automation',
        description: 'Intelligent systems and workflow automation that turn data into decisions.',
        accent: 'from-violet-400 to-purple-600',
        badge: 'bg-violet-500',
    },
    'E-commerce Platforms': {
        label: 'E-commerce Platforms',
        headline: 'E-commerce Platforms',
        description: 'Frictionless, high-conversion online retail experiences that turn browsers into buyers.',
        accent: 'from-emerald-400 to-teal-600',
        badge: 'bg-emerald-500',
    },
    'Startup Acceleration': {
        label: 'Startup Acceleration',
        headline: 'Startup Acceleration',
        description: 'Tech backbone and strategic guidance that helped new ventures launch, iterate, and scale.',
        accent: 'from-yellow-400 to-orange-500',
        badge: 'bg-yellow-500',
    },
    'Smart Technologies': {
        label: 'Smart Technologies',
        headline: 'Smart Technologies',
        description: 'IoT, automation, and connected solutions that bring intelligence to everyday environments.',
        accent: 'from-sky-400 to-indigo-500',
        badge: 'bg-sky-500',
    },
};

const CATEGORIES = Object.keys(CATEGORY_CONFIG);

// Animation variants
const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.07 } },
};
const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

// ── Helper: convert category name ↔ URL slug ─────────────────────────────────
const toSlug = (cat) => cat.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
const fromSlug = (slug) => CATEGORIES.find(c => toSlug(c) === slug) || 'All';

const ProjectsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Read active category from URL — e.g. ?category=digital-marketing
    const categorySlug = searchParams.get('category') || 'all';
    const activeCategory = fromSlug(categorySlug);
    const config = CATEGORY_CONFIG[activeCategory] || CATEGORY_CONFIG['All'];

    // Scroll to top whenever category changes via URL
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSearchQuery('');
    }, [categorySlug]);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}?resource=projects`);
                const data = await res.json();
                setProjects(data);
            } catch (err) {
                console.error('Error fetching projects:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProjects();
    }, []);

    const getImageSrc = (url) => {
        if (!url) return 'https://placehold.co/1200x800/1f2937/FFFFFF?text=No+Image';
        if (url.startsWith('data:image')) return url;
        if (url.startsWith('http')) return url;
        return `${IMAGE_BASE_URL}${url}`;
    };

    // Navigate to a category URL
    const handleCategoryClick = (cat) => {
        if (cat === 'All') {
            setSearchParams({});
        } else {
            setSearchParams({ category: toSlug(cat) });
        }
    };

    // Available categories based on real data
    const availableCategories = useMemo(() => {
        const fromData = [...new Set(projects.map(p => p.category).filter(Boolean))];
        return CATEGORIES.filter(c => c === 'All' || fromData.includes(c));
    }, [projects]);

    // Filtered + searched projects
    const filteredProjects = useMemo(() => {
        return projects.filter(p => {
            const matchCat = activeCategory === 'All' || p.category === activeCategory;
            const matchSearch =
                !searchQuery ||
                p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.category?.toLowerCase().includes(searchQuery.toLowerCase());
            return matchCat && matchSearch;
        });
    }, [projects, activeCategory, searchQuery]);

    return (
        <div className="min-h-screen bg-white dark:bg-black text-slate-900 dark:text-white">

            {/* ── Hero Banner ─────────────────────────────────────────────────── */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeCategory}
                    className="relative pt-36 pb-16 px-6 overflow-hidden bg-slate-50 dark:bg-gray-950"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {/* Decorative glows — colour adapts per category */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className={`absolute -top-32 -left-32 w-96 h-96 rounded-full blur-3xl opacity-20 bg-gradient-to-br ${config.accent}`} />
                        <div className={`absolute -bottom-20 right-0 w-80 h-80 rounded-full blur-3xl opacity-15 bg-gradient-to-bl ${config.accent}`} />
                    </div>

                    <div className="container mx-auto max-w-5xl relative z-10">
                        {/* Back link */}
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-cyan-500 transition-colors mb-8 group"
                        >
                            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-200" />
                            Back to Home
                        </Link>

                        {/* Page title: first word dark/white, rest cyan-400 */}
                        <motion.h1
                            className="text-5xl md:text-7xl font-extrabold tracking-tight mb-4"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
                        >
                            <span className="text-slate-900 dark:text-white">
                                {config.headline.split(' ')[0]}{' '}
                            </span>
                            <span className="text-cyan-400">
                                {config.headline.split(' ').slice(1).join(' ')}
                            </span>
                        </motion.h1>

                        {/* Description */}
                        <motion.p
                            className="text-slate-500 dark:text-gray-400 text-lg md:text-xl max-w-2xl mb-10"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.18, ease: 'easeOut' }}
                        >
                            {config.description}
                        </motion.p>

                        {/* Search bar */}
                        <motion.div
                            className="relative max-w-lg mb-10"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.25 }}
                        >
                            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-500" />
                            <input
                                id="projects-search"
                                type="text"
                                placeholder="Search projects..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-11 pr-5 py-3.5 rounded-xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 focus:border-cyan-500 dark:focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 transition-all duration-300 shadow-sm"
                            />
                        </motion.div>

                        {/* ── Category Filter Pills ── */}
                        <motion.div
                            className="flex flex-wrap gap-3"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.32 }}
                        >
                            {availableCategories.map((cat) => {
                                const isActive = activeCategory === cat;
                                return (
                                    <motion.button
                                        key={cat}
                                        onClick={() => handleCategoryClick(cat)}
                                        className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-250 border ${
                                            isActive
                                                ? 'bg-cyan-500 text-black border-cyan-500 shadow-md shadow-cyan-500/25'
                                                : 'bg-transparent text-slate-600 dark:text-gray-300 border-slate-300 dark:border-gray-600 hover:border-cyan-500 hover:text-cyan-500 dark:hover:text-cyan-400'
                                        }`}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.96 }}
                                        aria-pressed={isActive}
                                    >
                                        {cat}
                                        {cat === 'All' && (
                                            <span className="ml-2 text-xs opacity-70">({projects.length})</span>
                                        )}
                                    </motion.button>
                                );
                            })}
                        </motion.div>
                    </div>
                </motion.div>
            </AnimatePresence>


            {/* ── Projects Grid ────────────────────────────────────────────────── */}
            <div className="container mx-auto max-w-7xl px-6 py-16">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="rounded-xl overflow-hidden bg-slate-100 dark:bg-gray-900 animate-pulse">
                                <div className="h-52 bg-slate-200 dark:bg-gray-800" />
                                <div className="p-6 space-y-3">
                                    <div className="h-3 w-24 bg-slate-200 dark:bg-gray-700 rounded" />
                                    <div className="h-5 w-40 bg-slate-200 dark:bg-gray-700 rounded" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Result count */}
                        <motion.p
                            key={activeCategory + searchQuery}
                            className="text-sm text-slate-400 dark:text-gray-500 mb-8"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            Showing{' '}
                            <span className="text-cyan-500 font-semibold">{filteredProjects.length}</span>{' '}
                            {filteredProjects.length === 1 ? 'project' : 'projects'}
                            {activeCategory !== 'All' && ` in "${activeCategory}"`}
                            {searchQuery && ` matching "${searchQuery}"`}
                        </motion.p>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeCategory + searchQuery}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                            >
                                {filteredProjects.length > 0 ? (
                                    filteredProjects.map((project, index) => (
                                        <motion.div
                                            key={project.id || index}
                                            variants={cardVariants}
                                            className="group flex flex-col h-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-md hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-300 border border-slate-100 dark:border-gray-800 hover:border-cyan-500/40 cursor-pointer"
                                            whileHover={{ y: -6 }}
                                            transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                                            onClick={() => setSelectedProject(project)}
                                        >
                                            {/* Image */}
                                            <div
                                                className="relative w-full overflow-hidden"
                                                style={{ paddingTop: '56.25%' }}
                                            >
                                                <img
                                                    src={getImageSrc(project.image_url || project.image)}
                                                    alt={project.title}
                                                    className="absolute top-0 left-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://placehold.co/1200x800/1f2937/FFFFFF?text=Image+Not+Found';
                                                    }}
                                                />
                                                {/* Category badge appears on hover */}
                                                <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    <span className="px-3 py-1 rounded-full bg-cyan-500 text-white text-xs font-bold backdrop-blur-sm">
                                                        {project.category}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Text */}
                                            <div className="p-6 flex flex-col flex-grow">
                                                <p className="text-cyan-600 dark:text-cyan-400 font-semibold text-sm">
                                                    {project.category}
                                                </p>
                                                <h3 className="text-xl font-bold mt-1 text-slate-900 dark:text-white">
                                                    {project.title}
                                                </h3>
                                                {project.description && (
                                                    <p className="text-slate-500 dark:text-gray-400 text-sm mt-2 line-clamp-2">
                                                        {project.description}
                                                    </p>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))
                                ) : (
                                    <motion.div
                                        className="col-span-3 flex flex-col items-center justify-center py-28 text-slate-400 dark:text-gray-500"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                    >
                                        <div className="text-6xl mb-4">🔍</div>
                                        <p className="text-xl font-semibold mb-2">No projects found</p>
                                        <p className="text-sm">Try a different category or search term.</p>
                                        <button
                                            onClick={() => { handleCategoryClick('All'); setSearchQuery(''); }}
                                            className="mt-6 px-6 py-2.5 rounded-full border border-cyan-500 text-cyan-500 font-semibold text-sm hover:bg-cyan-500 hover:text-black transition-all duration-300"
                                        >
                                            Clear Filters
                                        </button>
                                    </motion.div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </>
                )}
            </div>

            {/* Project Details Modal Popup */}
            <AnimatePresence>
                {selectedProject && (
                    <ProjectDetailModal
                        project={selectedProject}
                        onClose={() => setSelectedProject(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default ProjectsPage;
